import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useSearch } from "@tanstack/react-router";

import { useSameRouteNavigate, type UrlRecord } from "@/routing/sameRoute";

/** Writes one name into the group's base64 blob, leaving the rest alone. */
type SetGroupedParam = (name: string, value: unknown) => void;

/** Base64 helpers with Node `Buffer` semantics, without `Buffer`. */
const decodeBase64 = (str: string): string => {
  const normalised = str.replace(/-/g, "+").replace(/_/g, "/");
  // Buffer treats the first `=` as end-of-data rather than as a strippable
  // pad character, so "a=b=c" decodes to "" and not to the bytes of "abc".
  const alphabetOnly = normalised.split("=")[0].replace(/[^A-Za-z0-9+/]/g, "");
  // A remainder of 1 cannot encode any whole byte; Buffer drops it.
  const usable = alphabetOnly.slice(
    0,
    alphabetOnly.length - (alphabetOnly.length % 4 === 1 ? 1 : 0)
  );
  return atob(usable.padEnd(Math.ceil(usable.length / 4) * 4, "="));
};

const encodeBase64 = (str: string): string => {
  let latin1 = "";
  for (let index = 0; index < str.length; index += 1) {
    latin1 += String.fromCharCode(str.charCodeAt(index) & 0xff);
  }
  return btoa(latin1);
};

function parseWithDate(jsonString: string): any {
  var reDateDetect = /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/; // startswith: 2015-04-29T22:06:55
  var resultObject = JSON.parse(jsonString, (_key: any, value: any) => {
    if (typeof value == "string" && reDateDetect.exec(value)) {
      return new Date(value);
    }
    return value;
  });
  return resultObject;
}

/** Returns a decoded object / {} depending on query variable. */
export function parseBase64(queryParam: string | undefined | null) {
  if (!queryParam) {
    return {};
  }
  try {
    return parseWithDate(decodeBase64(decodeURIComponent(queryParam)));
  } catch {
    //If old string throws a decoding error, return an empty array to re-initialize.
    return {};
  }
}

/** Returns a url safe encoded version of object. */
export function stringifyBase64(obj: any) {
  return encodeURIComponent(encodeBase64(JSON.stringify(obj)));
}

type QueryParamMapper<T> = {
  [K in keyof T]: [T[K], Dispatch<SetStateAction<T[K]>>, string];
};
/**
 * NOTE: This is actually a hook!
 *
 * Returns an object with stateful values for params, and corresponding function to update them making use of queryParams for initial values.
 * @param params Object with state names as keys, initial values.
 * @param group Name displayed on query parameters.
 * @deprecated
 */
export default function useQueryParamStates<S>(
  params: S,
  group: string
): QueryParamMapper<S> {
  const search = useSearch({ strict: false }) as UrlRecord;
  const setGroupedParam = useSetGroupedParam(group);

  //Object entries requires a cast.
  const groupedSetters = Object.entries(params as Object).reduce(
    (p, [key, value]) =>
      Object.assign(p, {
        // This kinda works because we have static params, but we should really avoid using this.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        [key]: useQueryParamState<typeof value>(
          value,
          search,
          setGroupedParam,
          key,
          group
        ),
      }),
    {}
  ) as QueryParamMapper<S>;
  // This is a hack to memoize the object above. If we used useMemo it would use hooks inside causing react errors
  // `search` stands in for the router identity this used to depend on: it is a
  // new object exactly when the URL changes, which is when the getters above go
  // stale and the memo has to be refreshed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => groupedSetters, [params, search, group]);
}

/**
 * Writes one name into the group's blob, reading the blob it is amending at
 * commit time rather than at render time. Two names written in the same tick
 * therefore compose instead of the second overwriting the first — see
 * `useSameRouteNavigate`.
 */
function useSetGroupedParam(group: string): SetGroupedParam {
  const navigateSameRoute = useSameRouteNavigate();

  return useCallback(
    (name, value) => {
      navigateSameRoute({
        search: (previous) => ({
          ...previous,
          //Add or override the hash code with the hash.
          [group]: stringifyBase64({
            ...parseBase64(previous[group]),
            [name]: value,
          }),
        }),
      });
    },
    [group, navigateSameRoute]
  );
}

/**
 * Returns base64 encoded string in query params with group name.
 * @param groupName Name displayed on query parameters.
 */
export function getBase64WithGroupName(
  groupName: string,
  search: Record<string, string | undefined>
): string | undefined {
  return search[groupName];
}

/**
 * Returns object in query params with group name.
 * @param groupName Name displayed on query parameters.
 */
function getStateWithGroupName(groupName: string, search: UrlRecord) {
  const base64EncodedString = getBase64WithGroupName(groupName, search);
  if (base64EncodedString) {
    const queryParamObject = parseBase64(base64EncodedString);
    return queryParamObject;
  }
  return undefined;
}

/**
 * Returns a stateful value, and a function to update it. Updated initialState depending on query params.
 * @param group Name displayed on query parameters.
 * @returns [getter, setter, name]
 */
function useQueryParamState<S>(
  initialState: S | (() => S),
  search: UrlRecord,
  setGroupedParam: SetGroupedParam,
  name: string,
  group: string
): [S, Dispatch<SetStateAction<S>>, string] {
  let startingState = initialState;
  //
  const queryParamObject = getStateWithGroupName(group, search);
  if (queryParamObject) {
    if (queryParamObject[name]) {
      startingState = queryParamObject[name];
    }
  }

  const [getter, setter] = useState<S>(startingState);

  function setterWrapper(action: SetStateAction<S>) {
    setGroupedParam(name, action);
    setter(action);
  }

  return [getter, setterWrapper, name];
}
